import { useLanguage } from '../i18n/LanguageContext';
import { Reveal, Overline } from './motion';

const FINISHES = [
  { key: 'alb', bg: '#F5F5F2', border: true },
  { key: 'maro', bg: '#6B4A2F' },
  { key: 'bronz', bg: 'linear-gradient(135deg, #9A7B4F 0%, #C7A96B 40%, #8C6B3F 100%)' },
  { key: 'titan', bg: 'linear-gradient(135deg, #8A8F94 0%, #B9BEC3 45%, #7A7F85 100%)' },
];

const WOOD = (a, b, c) => `repeating-linear-gradient(92deg, ${a} 0px, ${b} 7px, ${c} 14px, ${b} 21px)`;

const WOOD_COLORS = [
  { name: 'Stejar auriu', bg: WOOD('#7A4E20', '#C89B5F', '#8A5A28') },
  { name: 'Nuc', bg: WOOD('#33200F', '#5C4033', '#3B2419') },
  { name: 'Stejar închis', bg: WOOD('#241811', '#4A3020', '#2E1D14') },
  { name: 'Mahon', bg: WOOD('#401511', '#6B2B22', '#4C1A15') },
];

const RAL_COLORS = [
  { name: 'Alb', ral: '', bg: '#F5F5F2', border: true },
  { name: 'Alb crem', ral: 'RAL 9001', bg: '#EFE9DA' },
  { name: 'Gri antracit', ral: 'RAL 7016', bg: '#383E42' },
  { name: 'Gri agat', ral: 'RAL 7038', bg: '#A9AEAC' },
  { name: 'Gri deschis', ral: 'RAL 7035', bg: '#C9CCC8' },
  { name: 'Negru', ral: 'RAL 9005', bg: '#131416' },
  { name: 'Verde moinești', ral: 'RAL 6005', bg: '#12402F' },
  { name: 'Albastru oțel', ral: 'RAL 5011', bg: '#1E2A44' },
];

const FamilyTitle = ({ children }) => (
  <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-smoke mb-5">{children}</p>
);

export const Accessories = () => {
  const { t } = useLanguage();
  const a = t('accessories');
  const finishNames = a.finishNames;

  return (
    <section data-testid="accessories-section" className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
      <Reveal className="mb-12 md:mb-16">
        <Overline className="mb-4">{a.overline}</Overline>
        <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-4">{a.title}</h2>
        <p className="text-smoke max-w-2xl leading-relaxed">{a.desc}</p>
      </Reveal>

      <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start">
        <Reveal className="lg:col-span-2">
          <div
            data-testid="palette-card"
            className="group rounded-[2rem] bg-[#F7F5F1] dark:bg-[#F7F5F1] border border-line/15 p-6 md:p-8 shadow-[0_24px_60px_-24px_rgba(24,26,32,0.28)]"
          >
            <img
              src="/color-fan.webp"
              alt="Paletar culori Domidoor Solutions"
              data-testid="palette-image"
              loading="lazy"
              decoding="async"
              className="w-full h-auto transition-transform duration-700 ease-out group-hover:scale-[1.04] group-hover:rotate-1"
            />
            <p data-testid="palette-caption" className="mt-5 text-[#5A5D64] text-xs md:text-sm leading-relaxed border-l-2 border-accent pl-4">
              {a.paletteCaption}
            </p>
          </div>
        </Reveal>

        <div className="lg:col-span-3 space-y-10 md:space-y-12">
          <Reveal>
            <FamilyTitle>{a.finishesTitle}</FamilyTitle>
            <div className="flex flex-wrap gap-3">
              {FINISHES.map((f, i) => (
                <span
                  key={f.key}
                  data-testid={`finish-${f.key}`}
                  className="inline-flex items-center gap-2.5 rounded-full border border-line/20 bg-surface px-4 py-2 text-sm transition-colors duration-300 hover:border-accent/60"
                >
                  <span
                    className={`w-5 h-5 rounded-full shrink-0 ${f.border ? 'border border-line/25' : ''}`}
                    style={{ background: f.bg }}
                  />
                  {finishNames[i]}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <FamilyTitle>{a.woodTitle}</FamilyTitle>
            <div data-testid="wood-family" className="grid grid-cols-2 gap-4 md:gap-5">
              {WOOD_COLORS.map((c) => (
                <div key={c.name} data-testid={`wood-${c.name.toLowerCase().replace(/ /g, '-')}`} className="group">
                  <div
                    className="h-16 md:h-20 rounded-xl transition-transform duration-500 group-hover:-translate-y-1 group-hover:shadow-[0_16px_40px_rgba(24,26,32,0.15)]"
                    style={{ background: c.bg }}
                  />
                  <p className="font-display font-medium mt-2.5 text-sm">{c.name}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <FamilyTitle>{a.ralTitle}</FamilyTitle>
            <div data-testid="ral-family" className="grid grid-cols-4 gap-4 md:gap-5">
              {RAL_COLORS.map((c) => (
                <div key={c.name} data-testid={`color-${c.name.toLowerCase().replace(/ /g, '-')}`} className="group">
                  <div
                    className={`h-14 md:h-16 rounded-lg transition-transform duration-500 group-hover:-translate-y-1 group-hover:shadow-[0_16px_40px_rgba(24,26,32,0.15)] ${c.border ? 'border border-line/15' : ''}`}
                    style={{ background: c.bg }}
                  />
                  <p className="font-display font-medium mt-2 text-xs md:text-sm leading-tight">{c.name}</p>
                  {c.ral && <p className="text-smoke text-[10px] md:text-xs">{c.ral}</p>}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>

      <Reveal className="mt-12 md:mt-16">
        <p data-testid="accessories-note" className="text-smoke text-sm border-l-2 border-accent pl-4">{a.note}</p>
      </Reveal>
    </section>
  );
};
